/* eslint-disable react/display-name */

import * as XLSX from "xlsx";
import Box from "@material-ui/core/Box";
import CircularProgress from "@material-ui/core/CircularProgress";
import html2canvas from "html2canvas";
import moment from "moment";
import React, { FC, useMemo, useState } from "react";
import { Checkbox } from "@material-ui/core";
import { GridColDef } from "@material-ui/data-grid";
import { Theme } from "@material-ui/core/styles";
import { jsPDF } from "jspdf";
import { styled } from "@material-ui/styles";

import DataGrid from "../DataGrid";
import { SecondaryButton } from "../../elements/Button";
import {
  makeDateSortComparator,
  numericSortComparatorNaLowest,
  transformRowValue,
} from "../../../../helpers/campainRowFormatHelpers";

type InvoicesDashboardProps = {
  invoices: any[];
  isLoading?: boolean;
};

const Card = styled(Box)(({ theme }: { theme: Theme }) => ({
  background: theme.palette.brand.white,
  border: `1px solid ${theme.palette.brand.gray200}`,
  borderRadius: 8,
  overflow: "hidden",
}));

const Toolbar = styled("div")({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "16px 20px",
});

const CardTitle = styled("h2")(({ theme }: { theme: Theme }) => ({
  margin: 0,
  fontSize: 16,
  fontWeight: 600,
  color: theme.palette.brand.gray900,
}));

const INVOICE_TYPE_LABELS: Record<string, string> = {
  "ses-monthly-billing": "Monthly Billing",
  "ses-weekly-campaign-spend-billing": "Weekly Campaign Spend",
  "ses-weekly-domain-billing": "Weekly Domain Billing",
};

const GridWrapper = styled("div")({
  minHeight: 200,
});

const LoadingState = styled("div")({
  minHeight: 200,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

const EmptyState = styled("div")(({ theme }: { theme: Theme }) => ({
  minHeight: 200,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "32px 20px",
  fontSize: 14,
  color: theme.palette.brand.gray500,
}));

const DownloadIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

const InvoicesDashboard: FC<InvoicesDashboardProps> = ({ invoices, isLoading = false }) => {
  const [isExporting, setIsExporting] = useState(false);
  const [isExportingExcel, setIsExportingExcel] = useState(false);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);

  const rows = useMemo(
    () =>
      invoices.map(
        (
          {
            sentAt,
            recipientEmail,
            source,
            subject,
            body,
            brandgenAdvertiserId,
            advertiser,
            chargeAmount,
            templateName,
            templateData,
          },
          index,
        ) => ({
          id: `${sentAt}-${index}`,
          sentAt: moment(sentAt).format("MMM Do YYYY, h:mm:ss a"),
          // Hidden from the grid; the Date column sorts on this instead of its formatted string.
          sentAtRaw: moment(sentAt).valueOf(),
          type: INVOICE_TYPE_LABELS[templateName] ?? "—",
          chargeAmount: transformRowValue(chargeAmount, false, true, "CURRENCY"),
          recipientEmail,
          source,
          subject,
          body,
          brandgenAdvertiserId,
          advertiserName: advertiser?.name ?? "N/A",
          templateName,
          templateData,
        }),
      ),
    [invoices],
  );

  const isAllSelected = rows.length > 0 && selectedRows.length === rows.length;
  const isIndeterminate = selectedRows.length > 0 && selectedRows.length < rows.length;

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedRows(event.target.checked ? rows : []);
  };

  const handleRowCheckboxChange = (row: any) => (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedRows((prev) => [...prev, row]);
    } else {
      setSelectedRows((prev) => prev.filter((r) => r.id !== row.id));
    }
  };

  const columns: GridColDef[] = [
    {
      field: "checkbox",
      headerName: "Select All",
      disableColumnMenu: true,
      sortable: false,
      headerAlign: "center",
      align: "center",
      width: 60,
      renderHeader: () => (
        <Checkbox checked={isAllSelected} indeterminate={isIndeterminate} onChange={handleSelectAllClick} />
      ),
      renderCell: (params: any) => (
        <Checkbox
          color="primary"
          checked={selectedRows.some((r) => r.id === params.row.id)}
          onChange={handleRowCheckboxChange(params.row)}
        />
      ),
    },
    {
      field: "sentAt",
      headerName: "Date",
      width: 220,
      cellClassName: "primaryCell",
      sortComparator: makeDateSortComparator((row) => row.sentAtRaw),
    },
    { field: "type", headerName: "Type", width: 200, cellClassName: "secondaryCell" },
    {
      field: "chargeAmount",
      headerName: "Charge Amount",
      width: 180,
      cellClassName: "primaryCell",
      sortComparator: numericSortComparatorNaLowest,
    },
    { field: "recipientEmail", headerName: "Recipient Email", width: 250, cellClassName: "secondaryCell" },
    { field: "source", headerName: "Source", width: 250, cellClassName: "secondaryCell" },
    { field: "subject", headerName: "Subject", width: 250, cellClassName: "secondaryCell" },
    { field: "advertiserName", headerName: "Advertiser Name", width: 250, cellClassName: "secondaryCell" },
  ];

  const handleExport = async () => {
    setIsExporting(true);

    setTimeout(async () => {
      try {
        for (const row of selectedRows) {
          const tempDiv = document.createElement("div");
          tempDiv.style.position = "absolute";
          tempDiv.style.left = "-9999px";
          tempDiv.style.visibility = "visible";
          tempDiv.style.width = "800px";
          tempDiv.innerHTML = row.body;
          document.body.append(tempDiv);

          /* eslint-disable no-await-in-loop */
          const canvas = await html2canvas(tempDiv, {
            scale: 2,
            useCORS: true,
            onclone: (doc) =>
              new Promise<void>((resolve) => {
                const images = doc.querySelectorAll("img");
                let loadedCount = 0;

                if (images.length === 0) {
                  resolve();
                  return;
                }

                const checkDone = () => {
                  loadedCount++;
                  if (loadedCount === images.length) resolve();
                };

                images.forEach((img) => {
                  if (img.complete) {
                    checkDone();
                  } else {
                    img.addEventListener("load", checkDone);
                    img.addEventListener("error", checkDone);
                  }
                });
              }),
          });

          const imageData = canvas.toDataURL("image/png");
          // eslint-disable-next-line new-cap
          const pdf = new jsPDF("p", "mm", "a4");
          const pageWidth = pdf.internal.pageSize.getWidth();
          const imgHeight = (canvas.height * pageWidth) / canvas.width;
          pdf.addImage(imageData, "PNG", 0, 0, pageWidth, imgHeight);

          const formattedId = row.id.split(".")[0].replace(/:/g, "-");
          pdf.save(`Invoice-${formattedId}.pdf`);

          tempDiv.remove();
        }
      } catch (error) {
        console.error("Error exporting to PDF:", error);
      } finally {
        setIsExporting(false);
        setSelectedRows([]);
      }
    }, 0);
  };

  const buildExcelRows = (row: any): object[] => {
    const td = row.templateData ?? {};
    const base = {
      Date: row.sentAt,
      Type: INVOICE_TYPE_LABELS[row.templateName] ?? "—",
      "Invoice #": td.invoice ?? "",
      "Advertiser Name": td.advertiserName ?? row.advertiserName,
      "Billing Report Date": td.billingReportDate ?? "",
      Card: td.creditCardBrand ? `${td.creditCardBrand} ****${td.creditCardLast4}` : "",
      Domains: td.domains ?? "",
      "Recipient Email": row.recipientEmail,
    };

    if (Array.isArray(td.campaigns) && td.campaigns.length > 0) {
      return td.campaigns.map((c: any) => ({
        ...base,
        "Campaign Name": c.name ?? "",
        "Campaign Dates": c.campaignFlightDates ?? "",
        "Statement Descriptor": c.statementDescriptor ?? "",
        Amount: c.spend ?? "",
        Status: c.status ?? "",
      }));
    }

    return [
      {
        ...base,
        "Campaign Name": "",
        "Campaign Dates": "",
        "Statement Descriptor": td.statementDescriptor ?? "",
        Amount: td.spend ?? "",
        Status: td.status ?? "",
      },
    ];
  };

  const handleExportExcel = () => {
    setIsExportingExcel(true);
    try {
      const data = selectedRows.flatMap((row) => buildExcelRows(row));
      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Invoices");
      XLSX.writeFile(wb, "Invoices.xlsx");
      setSelectedRows([]);
    } catch (error) {
      console.error("Error exporting to Excel:", error);
    } finally {
      setIsExportingExcel(false);
    }
  };

  return (
    <Card>
      <Toolbar>
        <CardTitle>Invoices</CardTitle>
        <div style={{ display: "flex", gap: 8 }}>
          <SecondaryButton
            type="button"
            disabled={selectedRows.length === 0 || isExporting || isLoading}
            onClick={handleExport}
          >
            <DownloadIcon />
            {isExporting ? "Exporting…" : "Export PDF"}
          </SecondaryButton>
          <SecondaryButton
            type="button"
            disabled={selectedRows.length === 0 || isExportingExcel || isLoading}
            onClick={handleExportExcel}
          >
            <DownloadIcon />
            {isExportingExcel ? "Exporting…" : "Export Excel"}
          </SecondaryButton>
        </div>
      </Toolbar>
      <GridWrapper>
        {isLoading ? (
          <LoadingState>
            <CircularProgress size={32} />
          </LoadingState>
        ) : rows.length === 0 ? (
          <EmptyState>No records available.</EmptyState>
        ) : (
          <DataGrid columns={columns} rows={rows} autoHeight />
        )}
      </GridWrapper>
    </Card>
  );
};

export default InvoicesDashboard;
